import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { api } from '../api';

import AIcon from '../assets/icons/A.svg';
import CIcon from '../assets/icons/C.svg';
import DCIcon from '../assets/icons/DC.svg';
import DocIcon from '../assets/icons/DocIcon.svg';
import PIcon from '../assets/icons/P.svg';
import PencilTemplateIcon from '../assets/icons/PenciTemplate.svg';

import pencilIcon from '../assets/icons/pencil.svg';
import trashIcon from '../assets/icons/trash.svg';
import checkCircleIcon from '../assets/icons/checkcircle.svg';

const iconMap = {
  'A.svg': AIcon,
  'C.svg': CIcon,
  'DC.svg': DCIcon,
  'DocIcon.svg': DocIcon,
  'P.svg': PIcon,
  'PenciTemplate.svg': PencilTemplateIcon
};

const tidyField = {
  backgroundColor: '#2c2c2c',   // darker, matches rest of UI
  color: '#fff',
  border: '0px solid #555',
  borderRadius: '6px',
  fontFamily: 'inherit',
  padding: '10px 8px',
  outline: 'none',              // removes the blue outline
};

const TemplateDetails = ({ releaseDate }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [currentTab, setCurrentTab] = useState('Operations');
  const [editingTask, setEditingTask] = useState(null);

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);

  // Hard-coded department list
  const departments = ['Operations', 'Marketing', 'Socials+Creative', 'A&R'];

  // -----------------------------
  // 1) Fetch the template doc
  // -----------------------------
  useEffect(() => {
    if (id) {
      fetchTemplateById(id);
    }
  }, [id]);

  const fetchTemplateById = async (templateId) => {
    try {
      const res = await api(`/api/todotemplates/${templateId}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch template: ${res.status}`);
      }
      const data = await res.json();
      setTemplate(data);
    } catch (err) {
      console.error('Error fetching template:', err);
    }
  };

  // -----------------------------
  // 2) PUT (save) the entire template doc
  // -----------------------------
  const saveTemplate = async (updatedDoc) => {
    try {
      const res = await api(`/api/todotemplates/${updatedDoc._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedDoc)
      });
      if (!res.ok) {
        throw new Error(`Failed to update template: ${res.status}`);
      }
      // Optionally re-fetch so we’re guaranteed that local data 
      // matches the final DB doc
      await fetchTemplateById(updatedDoc._id);
    } catch (err) {
      console.error('Error saving template:', err);
    }
  };

  // Helper getters
  const getCategories = () => (template?.categories || []);
  const getTasks = () => (template?.tasks || []);

  // -----------------------------
  // 3) Switch department tab
  // -----------------------------
  const handleTabChange = (dept) => setCurrentTab(dept);

  // -----------------------------
  // 4) Category add/edit/delete
  // -----------------------------
  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      alert('Category name is required.');
      return;
    }
    const newCatId = crypto.randomUUID();
    const newCat = {
      _id: newCatId,
      name: newCategory,
      department: currentTab
    };
    const updatedCats = [...getCategories(), newCat];
    const updatedDoc = { ...template, categories: updatedCats };
    setShowCategoryModal(false);
    setNewCategory('');
    setEditingCategoryId(null);
    saveTemplate(updatedDoc);
  };

  const handleEditCategory = (catId) => {
    setEditingCategoryId(catId);
    const catObj = getCategories().find((c) => c._id === catId);
    if (catObj) {
      setNewCategory(catObj.name);
      setShowCategoryModal(true);
    }
  };

  const handleUpdateCategory = () => {
    if (!editingCategoryId || !newCategory.trim()) {
      alert('Category name is required.');
      return;
    }
    // Update categories
    const updatedCats = getCategories().map((cat) =>
      cat._id === editingCategoryId
        ? { ...cat, name: newCategory }
        : cat
    );
    const updatedDoc = { ...template, categories: updatedCats };
    setShowCategoryModal(false);
    setNewCategory('');
    setEditingCategoryId(null);
    saveTemplate(updatedDoc);
  };

  const handleDeleteCategory = (catId) => {
    // 1) Filter out tasks that belong to that category
    const updatedTasks = getTasks().filter(
      (t) => String(t.categoryId) !== String(catId)
    );
    // 2) Filter out the category itself
    const updatedCats = getCategories().filter(
      (c) => c._id !== catId
    );
    // 3) Save them together so the DB doc no longer has them
    const updatedDoc = {
      ...template,
      categories: updatedCats,
      tasks: updatedTasks
    };
    saveTemplate(updatedDoc);
  };

  // -----------------------------
  // 5) Tasks add/edit/delete
  // -----------------------------
  const handleAddTask = (catId) => {
    const newId = crypto.randomUUID();
    const newTask = {
      _id: newId,
      categoryId: catId,
      name: '',
      linkUrl: '', 
      daysBefore: '',
      status: 'Not Started',
      completedOn: null,
      dependencyTaskId: null,
      depDepartment: '',
      depCategoryId: null
    };
    const updatedTasks = [...getTasks(), newTask];
    const updatedDoc = { ...template, tasks: updatedTasks };
    saveTemplate(updatedDoc);
    setEditingTask(newTask);
  };

  const handleEditTask = (taskId) => {
    const tObj = getTasks().find((t) => t._id === taskId);
    if (!tObj) return;

    let depDepartment = '';
    let depCategoryId = null;
    if (tObj.dependencyTaskId) {
      const depTask = getTasks().find(
        (x) => x._id === tObj.dependencyTaskId
      );
      if (depTask) {
        const depCat = getCategories().find(
          (c) => String(c._id) === String(depTask.categoryId)
        );
        if (depCat) {
          depDepartment = depCat.department;
          depCategoryId = depCat._id;
        }
      }
    }
    setEditingTask({ ...tObj, depDepartment, depCategoryId });
  };

  const handleSaveTask = () => {
    if (!editingTask || !editingTask.name.trim()) {
      alert('Task name is required.');
      return;
    }
    const updatedTasks = getTasks().map((t) =>
      t._id === editingTask._id
        ? {
            ...t,
            name: editingTask.name,
            linkUrl: (editingTask.linkUrl || '').trim(),   // ⬅ NEW
            daysBefore: editingTask.daysBefore,
            status: editingTask.status,
            completedOn:
              editingTask.status === 'Completed' ? new Date().toISOString() : t.completedOn,
            dependencyTaskId: editingTask.dependencyTaskId || null
          }
        : t
    );
    const updatedDoc = { ...template, tasks: updatedTasks };
    saveTemplate(updatedDoc);
    setEditingTask(null);
  };

  const handleDeleteTask = (taskId) => {
    const updated = getTasks().filter((t) => t._id !== taskId);
    const updatedDoc = { ...template, tasks: updated };
    saveTemplate(updatedDoc);
  };

  const moveTask = (taskId, delta) => {
  const all = [...getTasks()];
  const idx = all.findIndex(t => t._id === taskId);
  const newIdx = idx + delta;
  if (idx < 0 || newIdx < 0 || newIdx >= all.length) return;   // guard

  all.splice(newIdx, 0, all.splice(idx, 1)[0]);                // reorder
  saveTemplate({ ...template, tasks: all });                    // PUT
};

const moveTaskUp   = (id) => moveTask(id, -1);
const moveTaskDown = (id) => moveTask(id,  1);

// ──────────────────────────────────────────────
// Category reordering (within the current dept)
// ──────────────────────────────────────────────
const moveCategory = (catId, delta) => {
  const all = [...getCategories()];
  // Build a list of indices in this department (in current array order)
  const deptIdxs = all
    .map((c, i) => ({ i, c }))
    .filter(x => x.c.department === currentTab)
    .map(x => x.i);

  // find the position of this category *within the department slice*
  const posInDept = deptIdxs.findIndex(i => all[i]._id === catId);
  if (posInDept < 0) return; // not found in this dept

  const targetPosInDept = posInDept + delta;
  if (targetPosInDept < 0 || targetPosInDept >= deptIdxs.length) return; // at edge

  // actual indices in the master array to swap
  const from = deptIdxs[posInDept];
  const to   = deptIdxs[targetPosInDept];

  // swap
  const tmp = all[from];
  all[from] = all[to];
  all[to]   = tmp;

  // persist
  saveTemplate({ ...template, categories: all });
};

const moveCategoryUp   = (catId) => moveCategory(catId, -1);
const moveCategoryDown = (catId) => moveCategory(catId,  1);

  const handleTaskEditChange = (e) => {
    const { name, value } = e.target;
    setEditingTask((prev) => ({ ...prev, [name]: value }));
  };

  const handleDaysBeforeChange = (val) => {
    setEditingTask((prev) => ({ ...prev, daysBefore: val }));
  };

  const handleStatusChange = (taskId, newStatus) => {
    const updated = getTasks().map((t) =>
      t._id === taskId
        ? {
            ...t,
            status: newStatus,
            completedOn: newStatus === 'Completed' ? new Date().toISOString() : null
          }
        : t
    );
    const updatedDoc = { ...template, tasks: updated };
    saveTemplate(updatedDoc);
  };

  // -----------------------------
  // 6) Dependency logic
  // -----------------------------
  const isDependencyCompleted = (task) => {
    if (!task.dependencyTaskId) return false;
    const depTask = getTasks().find((x) => x._id === task.dependencyTaskId);
    return depTask && depTask.status === 'Completed';
  };

  // Render the triple dropdown if editing
  const renderDependencySelectors = () => {
    if (!editingTask) return null;

    const handleDepDepartmentChange = (e) => {
      const depDept = e.target.value || '';
      setEditingTask((prev) => ({
        ...prev,
        depDepartment: depDept,
        depCategoryId: null,
        dependencyTaskId: null
      }));
    };
    const handleDepCategoryChange = (e) => {
      const catId = e.target.value || '';
      setEditingTask((prev) => ({
        ...prev,
        depCategoryId: catId,
        dependencyTaskId: null
      }));
    };
    const handleDepTaskChange = (e) => {
      const tId = e.target.value || '';
      setEditingTask((prev) => ({
        ...prev,
        dependencyTaskId: tId
      }));
    };

    const depDepartmentValue = editingTask.depDepartment || '';
    const possibleCategories = getCategories().filter(
      (c) => c.department === depDepartmentValue
    );

    let possibleTasks = [];
    if (editingTask.depCategoryId) {
      possibleTasks = getTasks().filter(
        (x) => String(x.categoryId) === String(editingTask.depCategoryId)
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <select
  value={depDepartmentValue}
  onChange={handleDepDepartmentChange}
  style={{
    ...tidyField,        // ← reuse the “no-border” look
    padding:'6px 8px',   // (slightly slimmer than the inputs)
    appearance:'none',   // remove native arrow styles (optional)
  }}
>
          <option value="">No Dept Selected</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </select>

        <select
  value={editingTask.depCategoryId || ''}
  onChange={handleDepCategoryChange}
  disabled={!depDepartmentValue}
  style={{ ...tidyField, padding:'6px 8px', appearance:'none' }}
>
          <option value="">No Category</option>
          {possibleCategories.map((c) => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>

        <select
  value={editingTask.dependencyTaskId || ''}
  onChange={handleDepTaskChange}
  disabled={!editingTask.depCategoryId}
  style={{ ...tidyField, padding:'6px 8px', appearance:'none' }}
>
          <option value="">No Task</option>
          {possibleTasks
            .filter((x) => x._id !== editingTask._id)
            .map((x) => (
              <option key={x._id} value={x._id}>
                {x.name}
              </option>
            ))}
        </select>
      </div>
    );
  };

  // -----------------------------
  // 7) Rendering calculations
  // -----------------------------
  const formatCompletedOnDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    });
  };

  const calculateDepartmentCompletion = (department) => {
    const catsInDept = getCategories().filter((c) => c.department === department);
    const tasksInDept = getTasks().filter((t) =>
      catsInDept.some((cat) => cat._id === t.categoryId)
    );
    if (!tasksInDept.length) return 0;
    const doneCount = tasksInDept.filter((t) => t.status === 'Completed').length;
    return (doneCount / tasksInDept.length) * 100;
  };

  const calculateCategoryCompletion = (catId) => {
    const catTasks = getTasks().filter((t) => t.categoryId === catId);
    if (!catTasks.length) return 0;
    const done = catTasks.filter((t) => t.status === 'Completed').length;
    return (done / catTasks.length) * 100;
  };

  // If you have a releaseDate prop:
  const calculateDueDate = (daysBefore) => {
    if (!releaseDate) return '-';
    if (!daysBefore) return '-';
    const rd = new Date(releaseDate);
    rd.setDate(rd.getDate() - parseInt(daysBefore, 10));
    return rd.toLocaleDateString('en-GB');
  };

  const getDueDateColor = (daysBefore) => {
    if (!releaseDate) return 'white';
    if (!daysBefore || isNaN(daysBefore)) return 'white';
    const d = new Date(releaseDate);
    d.setDate(d.getDate() - parseInt(daysBefore, 10));
    const diff = d - new Date();
    if (diff < 0) return 'red';
    if (diff <= 7 * 24 * 60 * 60 * 1000) return 'orange';
    return 'white';
  };

  if (!template) {
    return <div style={{ color: 'white' }}>Loading template...</div>;
  }

  const iconUrl = template.icon ? iconMap[template.icon] || null : null;

  return (
    <div style={{ padding: '20px', color: 'white' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/todo-templates')}
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          color: 'white',
          fontSize: '24px',
          cursor: 'pointer',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          style={{ marginRight: '8px', fill:'#333333' }}
        >
          <path d="M10 6 L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" transform="rotate(180,12,12)" />
        </svg>
      </button>

      {/* Icon & Title */}
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'40px' }}>
        {iconUrl ? (
          <img
            src={iconUrl}
            alt="Template Icon"
            style={{ width:'50px', height:'50px' }}
          />
        ) : (
          <div style={{ fontSize:'40px' }}>📄</div>
        )}
        <h1 style={{ margin:0 }}>{template.title}</h1>
      </div>

      {/* Department Tabs */}
      <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'20px' }}>
        {departments.map((dep) => {
          const completion = calculateDepartmentCompletion(dep);
          const isActive = currentTab === dep;
          return (
            <div
              key={dep}
              onClick={() => handleTabChange(dep)}
              style={{
                display:'flex',
                alignItems:'center',
                padding:'10px 15px',
                backgroundColor: isActive ? '#555' : '#181818',
                color:'white',
                borderRadius:'10px',
                cursor:'pointer',
                fontWeight:'bold',
                width:'200px',
                justifyContent:'space-between'
              }}
            >
              <span>{dep}</span>
              <div style={{ width:'30px', height:'30px' }}>
                <CircularProgressbar
                  value={completion}
                  styles={buildStyles({
                    pathColor:'#7eef4f',
                    trailColor:'#333',
                    strokeLinecap:'butt',
                    pathTransitionDuration:0.5
                  })}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Category */}
      <button
        onClick={() => {
          setNewCategory('');
          setEditingCategoryId(null);
          setShowCategoryModal(true);
        }}
        style={{
          padding:'10px 20px',
          backgroundColor:'#1e1e1e',
          color:'white',
          borderRadius:'5px',
          marginBottom:'20px',
          border:'none'
        }}
      >
        Add New Category
      </button>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width:'400px',
              padding:'20px',
              backgroundColor:'#1e1e1e',
              color:'white',
              borderRadius:'10px',
              margin:'auto'
            }}
          >
            <h2>{editingCategoryId ? 'Edit Category' : 'Add New Category'}</h2>
            <input
              type="text"
              placeholder="Category Name"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              style={{
                width:'100%',
                marginBottom:'10px',
                padding:'10px',
                backgroundColor:'#333',
                color:'white',
                border:'1px solid #555'
              }}
            />
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <button
                onClick={editingCategoryId ? handleUpdateCategory : handleAddCategory}
                style={{
                  padding:'10px 20px',
                  backgroundColor:'#28a745',
                  color:'white',
                  borderRadius:'5px',
                  border:'none'
                }}
              >
                {editingCategoryId ? 'Update' : 'Add'}
              </button>
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategoryId(null);
                  setNewCategory('');
                }}
                style={{
                  padding:'10px 20px',
                  backgroundColor:'#d9534f',
                  color:'white',
                  borderRadius:'5px',
                  border:'none'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories & Tasks */}
      <h3>{currentTab} Categories and Tasks</h3>
      {getCategories().filter((cat) => cat.department === currentTab).length === 0 ? (
        <p>No categories for {currentTab} yet.</p>
      ) : (
        getCategories()
          .filter((cat) => cat.department === currentTab)
          .map((category) => {
            const tasksInCat = getTasks().filter(
              (t) => String(t.categoryId) === String(category._id)
            );
            return (
              <div
                key={category._id}
                style={{
                  marginBottom:'20px',
                  padding:'20px',
                  backgroundColor:'#1e1e1e',
                  borderRadius:'10px'
                }}
              >
                {/* Category Header */}
                <div style={{ display:'flex', alignItems:'center' }}>
                  <h4 style={{ marginRight:'15px' }}>{category.name}</h4>
                  <div
                    style={{
                      width:'150px',
                      backgroundColor:'#333',
                      height:'10px',
                      borderRadius:'5px',
                      overflow:'hidden',
                      marginLeft:'10px'
                    }}
                  >
                    <div
                      style={{
                        width:`${calculateCategoryCompletion(category._id)}%`,
                        backgroundColor:'#7eef4f',
                        height:'100%'
                      }}
                    />
                  </div>
                  <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
  {/* ▲ move up */}
  {(() => {
    const visibleCats = getCategories().filter(c => c.department === currentTab);
    const idxInDept   = visibleCats.findIndex(c => c._id === category._id);
    const isFirst     = idxInDept === 0;
    const isLast      = idxInDept === visibleCats.length - 1;

    return (
      <>
        <button
          onClick={() => moveCategoryUp(category._id)}
          disabled={isFirst}
          title="Move category up"
          style={{
            background:'transparent',
            border:'none',
            cursor: isFirst ? 'default' : 'pointer',
            opacity: isFirst ? 0.25 : 1,
            color:'#565656',
            fontSize:16,
            lineHeight:'16px'
          }}
        >
          ▲
        </button>

        {/* ▼ move down */}
        <button
          onClick={() => moveCategoryDown(category._id)}
          disabled={isLast}
          title="Move category down"
          style={{
            background:'transparent',
            border:'none',
            cursor: isLast ? 'default' : 'pointer',
            opacity: isLast ? 0.25 : 1,
            color:'#565656',
            fontSize:16,
            lineHeight:'16px',
            marginRight:8
          }}
        >
          ▼
        </button>

        {/* Edit */}
        <button
          onClick={() => handleEditCategory(category._id)}
          style={{ background:'transparent', border:'none', cursor:'pointer', marginRight:'10px' }}
        >
          <img
            src={pencilIcon}
            alt="Edit"
            style={{
              width:'20px',
              height:'20px',
              filter: 'invert(33%) sepia(1%) saturate(0%) hue-rotate(163deg) brightness(97%) contrast(89%)'
            }}
          />
        </button>

        {/* Delete */}
        <button
          onClick={() => handleDeleteCategory(category._id)}
          style={{ background:'transparent', border:'none', cursor:'pointer' }}
        >
          <img
            src={trashIcon}
            alt="Delete"
            style={{
              width:'20px',
              height:'20px',
              filter: 'invert(33%) sepia(1%) saturate(0%) hue-rotate(163deg) brightness(97%) contrast(89%)'
            }}
          />
        </button>
      </>
    );
  })()}
</div>
                </div>

                {/* Task Table */}
                {tasksInCat.length === 0 ? (
                  <p>No tasks for {category.name} yet.</p>
                ) : (
                  <table
                    style={{
                      width:'100%',
                      borderCollapse:'collapse',
                      marginTop:'10px'
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor:'#333', color:'white' }}>
                        <th style={{ padding:'10px', textAlign:'left' }}>Task</th>
                        <th style={{ padding:'10px', textAlign:'left' }}>Days Before</th>
                        <th style={{ padding:'10px', textAlign:'left' }}>Due</th>
                        <th style={{ padding:'10px', textAlign:'left' }}>Done</th>
                        <th style={{ padding:'10px', textAlign:'left' }}>Completed On</th>
                        <th style={{ padding:'10px', textAlign:'left' }}>Dependency</th>
                        <th style={{ padding:'10px', textAlign:'left' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasksInCat.map((task) => {
                        const isEditing = editingTask && editingTask._id === task._id;
                        const dependencyDone = isDependencyCompleted(task);
                        const nameColor = dependencyDone ? '#7eef4f' : 'white';

                        // Build a label if not editing
                        let depLabel = 'None';
                        if (task.dependencyTaskId) {
                          const depTask = getTasks().find(
                            (dt) => dt._id === task.dependencyTaskId
                          );
                          if (depTask) {
                            const depCat = getCategories().find(
                              (c) => c._id === depTask.categoryId
                            );
                            if (depCat) {
                              depLabel = `${depCat.department} - ${depCat.name} - ${depTask.name}`;
                            } else {
                              depLabel = depTask.name;
                            }
                          }
                        }

                        return (
                          <tr
                            key={task._id}
                            style={{
                              borderBottom:'1px solid #555',
                              backgroundColor:'#1e1e1e'
                            }}
                          >
                           {/* Task Name (+ optional link) */}
<td style={{ padding:'10px', textAlign:'left' }}>
  {isEditing ? (
    <div style={{ display:'grid', gap:6 }}>
      <input
        type="text"
        name="name"
        value={editingTask.name}
        onChange={handleTaskEditChange}
        placeholder="Task name"
        style={{ ...tidyField, width: '100%' }}
      />
      <input
        type="url"
        name="linkUrl"
        value={editingTask.linkUrl || ''}
        onChange={handleTaskEditChange}
        placeholder="https://link-for-this-task.example"
        style={{ ...tidyField, width: '100%' }}
      />
    </div>
  ) : (
    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
      {dependencyDone && (
        <img
          src={checkCircleIcon}
          alt="Dependency Done"
          style={{
            width:'24px',
            height:'24px',
            filter:
              'brightness(0) saturate(100%) invert(53%) sepia(99%) ' +
              'saturate(252%) hue-rotate(62deg) brightness(105%) contrast(101%)'
          }}
        />
      )}
      {task.linkUrl ? (
        <a
          href={task.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: nameColor, fontWeight: 700, textDecoration: 'underline' }}
          title={task.linkUrl}
          onClick={(e) => e.stopPropagation()}
        >
          {task.name || task.linkUrl}
        </a>
      ) : (
        <strong style={{ color: nameColor }}>{task.name}</strong>
      )}
    </div>
  )}
</td>

                            {/* Days Before */}
                            <td style={{ padding:'10px', textAlign:'left' }}>
                              {isEditing ? (
                                <div style={{ display:'flex', alignItems:'center' }}>
                                  <input
  type="number"
  value={editingTask.daysBefore || ''}
  onChange={e => handleDaysBeforeChange(e.target.value)}
  style={{ ...tidyField, width: '80px', textAlign:'center' }}
/>
                                  <span style={{ marginLeft:'5px' }}>days before</span>
                                </div>
                              ) : (
                                <span>
                                  {task.daysBefore === ''
                                    ? '-'
                                    : task.daysBefore === '0'
                                    ? 'On Release Day'
                                    : `${task.daysBefore} days before release`}
                                </span>
                              )}
                            </td>

                            {/* Due */}
                            <td style={{
                              padding:'10px',
                              textAlign:'left',
                              color:getDueDateColor(task.daysBefore)
                            }}>
                              {calculateDueDate(task.daysBefore)}
                            </td>

                            {/* Done checkbox */}
<td style={{ padding:'10px', textAlign:'left' }}>
  <label style={{ cursor:'pointer', display:'inline-block' }}>
    {/* native checkbox (hidden) */}
    <input
      type="checkbox"
      checked={isEditing
        ? editingTask.status === 'Completed'
        : task.status     === 'Completed'}
      onChange={(e) => {
        const newStatus = e.target.checked ? 'Completed' : 'Not Started';
        if (isEditing) {
          setEditingTask(prev => ({
            ...prev,
            status: newStatus,
            completedOn: e.target.checked ? new Date().toISOString() : null
          }));
        } else {
          handleStatusChange(task._id, newStatus);
        }
      }}
      style={{ display:'none' }}
    />

    {/* custom box */}
    <span style={{
      display:'inline-block',
      width:24,
      height:24,
      border:'2px solid',
      borderColor:(
        isEditing
          ? editingTask.status === 'Completed'
          : task.status     === 'Completed'
      ) ? '#7eef4f' : '#555',           /* gray → green */
      borderRadius:4,
      background:(
        isEditing
          ? editingTask.status === 'Completed'
          : task.status     === 'Completed'
      ) ? '#7eef4f' : 'transparent',    /* fill turns green */
      transition:'all .15s ease',
      position:'relative'
    }}>
      {/* tick icon (only when checked) */}
      {(isEditing
        ? editingTask.status === 'Completed'
        : task.status     === 'Completed') && (
        <svg
          viewBox="0 0 24 24"
          style={{
            position:'absolute',
            top:-2,
            left:-2,
            width:26,
            height:26,
            fill:'none',
            stroke:'#1e1e1e',
            strokeWidth:3,
            pointerEvents:'none'
          }}
        >
          <path d="M5 13l4 4L19 7" />
        </svg>
      )}
    </span>
  </label>
</td>

                            {/* Completed On */}
                            <td style={{ padding:'10px', textAlign:'left' }}>
                              {task.completedOn
                                ? formatCompletedOnDate(task.completedOn)
                                : '-'}
                            </td>

                            {/* Dependency */}
                            <td style={{ padding:'10px', textAlign:'left' }}>
                              {isEditing
                                ? renderDependencySelectors()
                                : depLabel
                              }
                            </td>

                            {/* Actions */}
<td style={{ padding:'10px', textAlign:'left', whiteSpace:'nowrap' }}>
  {isEditing ? (
    <>
      {/* Save / Cancel while editing */}
      <button
        onClick={handleSaveTask}
        style={{
          backgroundColor:'#28a745',
          color:'white',
          padding:'5px 10px',
          borderRadius:'5px',
          cursor:'pointer',
          marginRight:'5px',
          border:'none'
        }}
      >
        Save
      </button>
      <button
        onClick={() => setEditingTask(null)}
        style={{
          backgroundColor:'#d9534f',
          color:'white',
          padding:'5px 10px',
          borderRadius:'5px',
          cursor:'pointer',
          border:'none'
        }}
      >
        Cancel
      </button>
    </>
  ) : (
    <>
      {/* ▲ move up */}
      <button
        onClick={() => moveTaskUp(task._id)}
        disabled={tasksInCat[0]._id === task._id}
        style={{
          background:'transparent',
          border:'none',
          cursor: tasksInCat[0]._id === task._id ? 'default' : 'pointer',
          opacity: tasksInCat[0]._id === task._id ? 0.25 : 1,
          marginRight:'4px',
          fontSize:'16px',
          lineHeight:'16px',
          color:'#565656'
        }}
      >
        ▲
      </button>

      {/* ▼ move down */}
      <button
        onClick={() => moveTaskDown(task._id)}
        disabled={tasksInCat[tasksInCat.length-1]._id === task._id}
        style={{
          background:'transparent',
          border:'none',
          cursor: tasksInCat[tasksInCat.length-1]._id === task._id ? 'default' : 'pointer',
          opacity: tasksInCat[tasksInCat.length-1]._id === task._id ? 0.25 : 1,
          marginRight:'10px',
          fontSize:'16px',
          lineHeight:'16px',
          color:'#565656'
        }}
      >
        ▼
      </button>

      {/* edit */}
      <button
        onClick={() => handleEditTask(task._id)}
        style={{
          background:'transparent',
          border:'none',
          cursor:'pointer',
          marginRight:'5px'
        }}
      >
        <img
  src={pencilIcon}
  alt="Edit"
  style={{
    width:'20px',
    height:'20px',
    filter: 'invert(33%) sepia(1%) saturate(0%) hue-rotate(163deg) ' +
            'brightness(97%) contrast(89%)'   // → #565656
  }}
/>
      </button>

      {/* delete */}
      <button
        onClick={() => handleDeleteTask(task._id)}
        style={{ background:'transparent', border:'none', cursor:'pointer' }}
      >
        <img
  src={trashIcon}
  alt="Delete"
  style={{
    width:'20px',
    height:'20px',
    filter: 'invert(33%) sepia(1%) saturate(0%) hue-rotate(163deg) ' +
            'brightness(97%) contrast(89%)'
  }}
        />
      </button>
    </>
  )}
</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <button
                  onClick={() => handleAddTask(category._id)}
                  style={{
                    marginTop:'10px',
                    padding:'10px 20px',
                    backgroundColor:'#555',
                    color:'white',
                    borderRadius:'5px',
                    fontFamily:'inherit',
                    cursor:'pointer',
                    border:'none'
                  }}
                >
                  Add Task
                </button>
              </div>
            );
          })
      )}
    </div>
  );
};

export default TemplateDetails;